import React from 'react';
import { Button, Message, Stack, mq } from '@kvdbil/components';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useRecaptcha } from '~/App/shared/components/Auth/hooks/authHooks';
import {
  EmailField,
  PhoneNumberField
} from '~/App/shared/components/Fields/shared';
import { fieldPropsFullName } from '~/App/shared/components/Fields/shared/fieldProps';
import CheckBoxWithControl from '~/App/shared/components/Fields/CheckBoxWithControl';
import TextFieldWithControl from '~/App/shared/components/Fields/TextFieldWithControl';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import { LeasingPartnerPresentationFragment } from '~/config/generated/graphql';
import { regexPhoneNumber } from '~/helpers/regex';
import { useTranslation } from '~/Locale';

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 1rem;

  ${mq('tablet')} {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

export type LeasingInterestFormValues = {
  fullName: string;
  email: string;
  phoneNumber: string;
  hasConsented: boolean;
};

type Props = {
  onSubmit: (values: LeasingInterestFormValues) => Promise<void> | void;
  onCancel: () => void;
  submitError?: string;
  consentLabel: LeasingPartnerPresentationFragment['interestConsentLabel'];
  submitButtonText: LeasingPartnerPresentationFragment['interestSubmitButtonText'];
};

const LeasingInterestForm = ({
  onSubmit,
  onCancel,
  submitError,
  consentLabel,
  submitButtonText
}: Props) => {
  const { t } = useTranslation();
  useRecaptcha();

  const { control, handleSubmit, formState } =
    useForm<LeasingInterestFormValues>({
      mode: 'onTouched',
      defaultValues: {
        fullName: '',
        email: '',
        phoneNumber: '',
        hasConsented: false
      }
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack rowGap="1rem">
        <TextFieldWithControl
          control={control}
          {...fieldPropsFullName({ t })}
        />
        <EmailField control={control} t={t} />
        <PhoneNumberField
          control={control}
          t={t}
          rules={{
            pattern: {
              value: regexPhoneNumber,
              message: t('Invalid phone number')
            }
          }}
        />
        <CheckBoxWithControl
          control={control}
          name="hasConsented"
          rules={{ required: true }}
          label={
            <StructuredText
              data={consentLabel}
              options={{ paragraphElem: 'span' }}
            />
          }
          color="info"
          colorShade="dark"
        />

        {submitError && <Message type="error">{submitError}</Message>}

        <ButtonRow>
          <Button
            type="button"
            color="secondary"
            variant="flat"
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            isDisabled={!formState.isValid}
            isLoading={formState.isSubmitting}
          >
            {submitButtonText}
          </Button>
        </ButtonRow>
      </Stack>
    </form>
  );
};

export default LeasingInterestForm;
